import type { HomeyAPI, AdvancedFlow } from 'homey-api';

import { BaseApiStore, type FetchArgs } from '../BaseApiStore.ts';
import { __DEV__ } from '../../lib/__dev__';

export type AdvancedFlowProps = {
  [K in keyof AdvancedFlow as AdvancedFlow[K] extends Function ? never : K]: AdvancedFlow[K];
};

type AdvancedFlowsData = { [key: string]: AdvancedFlow };
type AdvancedFlowsById = { [key: string]: AdvancedFlowProps };

interface State {
  data: AdvancedFlowsData | null;
  byId: AdvancedFlowsById | null;
  isLoading: boolean;
  error: unknown;
}

export type AdvancedFlowsStoreState = State;

export class AdvancedFlowsStore extends BaseApiStore<State, AdvancedFlow> {
  constructor() {
    super('advanced-flows');
  }

  override createInitialState() {
    return {
      data: null,
      byId: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, silent, cache, abortController }: FetchArgs) {
    if (silent === false) {
      this.set(this.createInitialState());
    }

    try {
      await this.connectManager({ api, manager: api.flow, reference: this });

      if (abortController.signal.aborted) return;

      const advancedFlowsPromise = api.flow.getAdvancedFlows({ $cache: cache });

      const [advancedFlowsData] = await Promise.all([advancedFlowsPromise]);

      if (abortController.signal.aborted) return;

      const advancedFlows = Object.values(advancedFlowsData);

      const data: AdvancedFlowsData = {};
      const byId: AdvancedFlowsById = {};

      for (const advancedFlowInstance of advancedFlows) {
        const advancedFlowReference = { ...advancedFlowInstance };

        data[advancedFlowReference.id] = advancedFlowInstance;
        byId[advancedFlowReference.id] = advancedFlowReference;
      }

      this.set({
        ...this.createInitialState(),
        data: data,
        byId: byId,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.set({
        ...this.createInitialState(),
        isLoading: false,
        error,
      });
    }
  }

  override destroy(reason: string) {
    try {
      const api = this.getApi();
      this.disconnectManager({ api, manager: api.flow, reference: this }).catch(console.error);
    } catch (error) {}
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.flow.on('advancedflow.create', this.handleCreate);
    api.flow.on('advancedflow.update', this.handleUpdate);
    api.flow.on('advancedflow.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.flow.off('advancedflow.create', this.handleCreate);
    api.flow.off('advancedflow.update', this.handleUpdate);
    api.flow.off('advancedflow.delete', this.handleDelete);
  }

  getInstanceFromCache(advancedFlow: { id: string }) {
    return this.getCache()[advancedFlow.id];
  }

  getCache(): { [key: string]: AdvancedFlow } {
    const api = this.getApi();
    const cache = api.flow.__cache['advancedflow'];
    return cache;
  }

  handleCreate = (createdAdvancedFlow: AdvancedFlow) => {
    this.DEBUG && console.info(`create:advancedflow:${createdAdvancedFlow.id}`);
    const state = this.get();
    const advancedFlowInstance = this.getInstanceFromCache(createdAdvancedFlow);

    if (advancedFlowInstance == null) return;

    const advancedFlowReference = { ...advancedFlowInstance };

    this.set({
      data: {
        ...state.data,
        [createdAdvancedFlow.id]: advancedFlowInstance,
      },
      byId: {
        ...state.byId,
        [createdAdvancedFlow.id]: advancedFlowReference,
      },
    });
  };

  handleUpdate = (updatedAdvancedFlow: AdvancedFlow) => {
    this.DEBUG && console.info(`update:advancedflow:${updatedAdvancedFlow.id}`);

    this.addToQueue(updatedAdvancedFlow.id, updatedAdvancedFlow);

    this.batch((updateQueue) => {
      const state = this.get();

      const nextById = {
        ...state.byId,
      };

      let updateCount = 0;

      for (const [id, queuedAdvancedFlow] of Object.entries(updateQueue)) {
        const advancedFlowInstance = this.getInstanceFromCache(queuedAdvancedFlow);

        if (state.data == null) {
          this.DEBUG && console.info(`update:advancedflow:missing-data`);
          continue;
        }

        if (advancedFlowInstance == null || state.data[id] == null) {
          this.DEBUG && console.info(`update:advancedflow:unknown:${id}`);
          continue;
        }

        const advancedFlowReference = { ...advancedFlowInstance };

        nextById[id] = advancedFlowReference;

        updateCount++;
      }

      this.DEBUG && console.info(`update:advancedflow:batch:count:${updateCount}`);

      updateCount > 0 &&
        this.set({
          byId: nextById,
        });
    });
  };

  handleDelete = (deletedAdvancedFlow: AdvancedFlow) => {
    this.DEBUG && console.info(`delete:advancedflow:${deletedAdvancedFlow.id}`);
    const state = this.get();

    if (state.data == null) {
      __DEV__ && console.log(`delete:advancedflow:missing-data`);
      return;
    }

    const advancedFlowInstance = state.data[deletedAdvancedFlow.id];

    if (advancedFlowInstance == null) return;

    const nextData = { ...state.data };
    const nextById = { ...state.byId };

    delete nextData[advancedFlowInstance.id];
    delete nextById[advancedFlowInstance.id];

    this.set({
      data: nextData,
      byId: nextById,
    });
  };

  restore() {
    const cache = this.getCache();
    const advancedFlows = Object.values(cache ?? {});
    const data: AdvancedFlowsData = {};
    const byId: AdvancedFlowsById = {};

    for (const advancedFlowInstance of advancedFlows) {
      const advancedFlowReference = { ...advancedFlowInstance };
      data[advancedFlowReference.id] = advancedFlowInstance;
      byId[advancedFlowReference.id] = advancedFlowReference;
    }

    this.set({
      data: data,
      byId: byId,
    });
  }
}

export const advancedFlowsStore = new AdvancedFlowsStore();
